import { FC, HTMLAttributes } from 'react'
import Image from 'next/image'
import logo from '@/public/logo.png'
import cohere from '@/public/providers/cohere.png'
import databricks from '@/public/providers/databricks.png'
import groq from '@/public/providers/groq.png'
import meta from '@/public/providers/meta.png'
import mistral from '@/public/providers/mistral.png'
import perplexity from '@/public/providers/perplexity.png'
import { ModelProvider } from '@/types'
import { IconSparkle2 } from '@tabler/icons-react'
import { useTheme } from 'next-themes'
import { Chat } from 'openai/resources/index.mjs'

import { cn } from '@/lib/utils'
import { MicrosoftSVG } from '@/components/icons/microsoft-svg'
import { MoonshotSVG } from '@/components/icons/moonshot-svg'
import { ProviderMonogramSVG } from '@/components/icons/provider-monogram-svg'

import { AnthropicSVG } from '../icons/anthropic-svg'
import { ChatbotUISVG } from '../icons/chatbotui-svg'
import { GoogleSVG } from '../icons/google-svg'
import { OpenAISVG } from '../icons/openai-svg'

interface ModelIconProps extends HTMLAttributes<HTMLDivElement> {
  provider: ModelProvider
  modelId?: string
  height: number
  width: number
}

export const ModelIcon: FC<ModelIconProps> = ({
  provider,
  height,
  width,
  modelId,
  className,
  ...props
}) => {
  const { theme } = useTheme()

  switch (provider as ModelProvider) {
    case 'marshmallow':
      return (
        <Image className={cn('rounded-sm')} src={logo} alt="Losi" width={width} height={height} />
      )
    case 'openai':
      return (
        <OpenAISVG
          className={cn(
            'rounded-sm bg-white p-1 text-black',
            className,
            theme === 'dark' ? 'bg-white' : 'border-foreground/10 border'
          )}
          width={width}
          height={height}
        />
      )
    case 'cohere':
      return (
        <Image
          className={cn(
            'rounded-sm p-1',
            theme === 'dark' ? 'bg-white' : 'border-foreground/10 border',
            className
          )}
          src={cohere}
          alt="Cohere"
          width={width}
          height={height}
        />
      )
    // @ts-ignore
    case 'mistralai':
    case 'mistral':
      return (
        <Image
          className={cn(
            'rounded-sm p-1',
            theme === 'dark' ? 'bg-white' : 'border-foreground/10 border',
            className
          )}
          src={mistral}
          alt="Mistral"
          width={width}
          height={height}
        />
      )
    case 'groq':
      return (
        <Image
          className={cn(
            'rounded-sm p-0',
            theme === 'dark' ? 'bg-white' : 'border-foreground/10 border',
            className
          )}
          src={groq}
          alt="Groq"
          width={width}
          height={height}
        />
      )
    case 'anthropic':
      return (
        <AnthropicSVG
          className={cn(
            'rounded-sm bg-white p-1 text-black',
            className,
            theme === 'dark' ? 'bg-white' : 'border-foreground/10 border'
          )}
          width={width}
          height={height}
        />
      )
    case 'google':
      return (
        <GoogleSVG
          className={cn(
            'rounded-sm bg-white p-1 text-black',
            className,
            theme === 'dark' ? 'bg-white' : 'border-foreground/10 border'
          )}
          width={width}
          height={height}
        />
      )
    case 'perplexity':
      return (
        <Image
          className={cn(
            'rounded-sm p-1',
            theme === 'dark' ? 'bg-white' : 'border-foreground/10 border',
            className
          )}
          src={perplexity}
          alt="Mistral"
          width={width}
          height={height}
        />
      )
    case 'databricks':
      return (
        <Image
          className={cn(
            'rounded-sm p-1',
            theme === 'dark' ? 'bg-white' : 'border-foreground/10 border',
            className
          )}
          src={databricks}
          alt="Databricks"
          width={width}
          height={height}
        />
      )
    // @ts-ignore
    case 'google ai studio':
    // @ts-ignore
    case 'azure':
    case 'microsoft':
      return (
        <MicrosoftSVG
          className={cn(
            'rounded-sm bg-white p-1 text-black',
            className,
            theme === 'dark' ? 'bg-white' : 'border-foreground/10 border'
          )}
          width={width}
          height={height}
        />
      )
    // @ts-ignore
    case 'meta-llama':
    case 'meta':
      return (
        <Image
          className={cn(
            'rounded-sm p-1',
            theme === 'dark' ? 'bg-white' : 'border-foreground/10 border',
            className
          )}
          src={meta}
          alt="Meta Llama"
          width={width}
          height={height}
        />
      )
    case 'openrouter':
      // TODO console.log("modelId", modelId)
      const newProvider = modelId?.split('/')[0]
      // TODO console.log("newProvider", newProvider)
      return (
        <ModelIcon
          className={className}
          provider={newProvider as ModelProvider}
          height={height}
          width={width}
        />
      )
    case 'bedrock':
      // AWS Bedrock groups several different companies' models under one
      // provider tag — branch on the model id prefix for the right company
      // icon, same pattern as openrouter above.
      if (modelId?.startsWith('zai.')) {
        return (
          <ProviderMonogramSVG
            className={className}
            label="Z"
            background="#4338CA"
            height={height}
            width={width}
          />
        )
      }
      if (modelId?.startsWith('deepseek.')) {
        return (
          <ProviderMonogramSVG
            className={className}
            label="D"
            background="#155DFC"
            height={height}
            width={width}
          />
        )
      }
      if (modelId?.startsWith('moonshotai.')) {
        return <MoonshotSVG className={className} height={height} width={width} />
      }
      if (modelId?.startsWith('amazon.')) {
        return (
          <ProviderMonogramSVG
            className={className}
            label="N"
            background="#FF9900"
            textColor="#232F3E"
            height={height}
            width={width}
          />
        )
      }
      return (
        <Image className={cn('rounded-sm')} src={logo} alt="Losi" width={width} height={height} />
      )
    default:
      return (
        <Image className={cn('rounded-sm')} src={logo} alt="Losi" width={width} height={height} />
      )
  }
}
